import { Directive, Injector, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker'
import { of, Subscription } from 'rxjs'

import { Permission } from '../../common/generated-types'
import { DataService } from '../../data/providers/data.service'
import { HealthCheckService } from '../../providers/health-check/health-check.service'
import {
  ActionBarContext,
  NavMenuItem
} from '../../providers/nav-builder/nav-builder-types'
import { NavBuilderService } from '../../providers/nav-builder/nav-builder.service'
import { NotificationService } from '../../providers/notification/notification.service'

@Directive({
  selector: '[vdrBaseNav]'
})
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class BaseNavComponent implements OnInit, OnDestroy {
  constructor(
    protected route: ActivatedRoute,
    protected router: Router,
    public navBuilderService: NavBuilderService,
    protected healthCheckService: HealthCheckService,
    protected dataService: DataService,
    protected notificationService: NotificationService,
    protected injector: Injector
  ) {}

  private userPermissions: string[]
  private subscription: Subscription

  shouldDisplayLink(menuItem: Pick<NavMenuItem, 'requiresPermission'>) {
    if (!this.userPermissions) {
      return false
    }
    if (!menuItem.requiresPermission) {
      return true
    }
    if (typeof menuItem.requiresPermission === 'string') {
      return this.userPermissions.includes(menuItem.requiresPermission)
    }
    if (typeof menuItem.requiresPermission === 'function') {
      return menuItem.requiresPermission(this.userPermissions)
    }
  }

  ngOnInit(): void {
    this.defineNavMenu()
    this.subscription = this.dataService.client
      .userStatus()
      .mapStream(({ userStatus }) => {
        this.userPermissions = userStatus.permissions
      })
      .subscribe()
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  getRouterLink(item: NavMenuItem) {
    return this.navBuilderService.getRouterLink(
      { routerLink: item.routerLink, context: this.createContext() },
      this.route
    )
  }

  private defineNavMenu() {
    function allow(
      ...permissions: string[]
    ): (userPermissions: string[]) => boolean {
      return (userPermissions) => {
        for (const permission of permissions) {
          if (userPermissions.includes(permission)) {
            return true
          }
        }
        return false
      }
    }

    this.navBuilderService.defineNavMenuSections([
      {
        id: 'dashboard',
        items: [
          {
            id: 'dashboard',
            label: 'Trang chủ',
            routerLink: ['/'],
            icon: 'home-smile'
          }
        ]
      },
      {
        id: 'room-calendar',
        requiresPermission: allow(Permission.ReadHotelCategory),
        items: [
          {
            requiresPermission: allow(Permission.ReadHotelCategory),
            id: 'room-calendar',
            label: 'Lịch phòng',
            routerLink: ['/hotel', 'calendar'],
            icon: 'calendar'
          }
        ]
      },
      {
        id: 'hotel',
        requiresPermission: allow(Permission.ReadHotel),
        items: [
          {
            requiresPermission: allow(Permission.ReadHotel),
            id: 'hotel',
            label: 'Quản lý khách sạn',
            routerLink: ['/hotel'],
            icon: 'hotel'
          }
        ]
      },
      {
        id: 'product',
        requiresPermission: allow(Permission.ReadProductHotel),
        items: [
          {
            requiresPermission: allow(Permission.ReadProductHotel),
            id: 'product-list',
            label: 'Quản lý sản phẩm',
            routerLink: ['/products', 'product'],
            icon: 'clipboard'
          }
        ]
      },
      {
        id: 'order-hotel',
        requiresPermission: allow(Permission.ReadBookingHotel),
        items: [
          {
            requiresPermission: allow(Permission.ReadBookingHotel),
            id: 'orders',
            label: 'Quản lý đặt phòng',
            routerLink: ['/orders-hotel'],
            icon: 'plate'
          }
        ]
      },
      {
        id: 'report-booking-hotel',
        requiresPermission: allow(Permission.ReportBooking),
        items: [
          {
            requiresPermission: allow(Permission.ReportBooking),
            id: 'report-booking-hotel',
            label: _('nav.report-booking'),
            routerLink: ['/report-booking-hotel'],
            icon: 'chart'
          }
        ]
      },
      {
        id: 'revenue-hotel',
        requiresPermission: allow(Permission.RevenueManagementHotelPermission),
        items: [
          {
            requiresPermission: allow(
              Permission.RevenueManagementHotelPermission
            ),
            id: 'hotel-commission',
            label: _('nav.revenue-commission'),
            icon: 'pie-chart',
            routerLink: ['/revenue-hotel', 'commission']
          }
        ]
      },
      {
        id: 'review-hotel',
        requiresPermission: allow(Permission.ReadRatingHotel),
        items: [
          {
            requiresPermission: allow(Permission.ReadRatingHotel),
            id: 'review-hotel',
            label: _('nav.reviews'),
            routerLink: ['/review-hotel'],
            icon: 'sort'
          }
        ]
      },
      {
        id: 'merchant-faq',
        items: [
          {
            id: 'merchant-faq',
            label: 'FAQ',
            routerLink: ['/merchant-faq'],
            icon: 'introduction'
          }
        ]
      },
      {
        id: 'feedback',
        items: [
          {
            id: 'feedback',
            label: 'Ý kiến đóng góp',
            routerLink: ['/feedback'],
            icon: 'chat-bubble'
          }
        ]
      },
      {
        id: 'chat',
        requiresPermission: allow(Permission.ReadGalaxyNotification),
        items: [
          {
            id: 'chat',
            label: 'Thông báo',
            requiresPermission: allow(Permission.ReadGalaxyNotification),
            routerLink: ['/chat-management'],
            icon: 'dialog'
          }
        ]
      },
      // {
      //   id: 'promotion',
      //   requiresPermission: allow(Permission.ReadPromotion),
      //   items: [
      //     {
      //       id: 'promotion',
      //       label: 'Quản lý khuyến mãi',
      //       requiresPermission: allow(Permission.ReadPromotion),
      //       routerLink: ['/promotion'],
      //       icon: 'dialog'
      //     }
      //   ]
      // },
      {
        id: 'settings',
        label: 'Thiết lập',
        icon: 'settings',
        collapsible: true,
        requiresPermission:
          allow(Permission.ReadAdministrator) || allow(Permission.ReadRole),
        collapsedByDefault: true,
        items: [
          {
            requiresPermission: allow(Permission.ReadAdministrator),
            id: 'employees',
            label: 'Quản lý nhân viên',
            routerLink: ['/settings', 'employees']
          },
          {
            requiresPermission: allow(Permission.CreateRole),
            id: 'roles',
            label: 'Phân quyền',
            routerLink: ['/settings', 'roles']
          }
        ]
      }
    ])
  }

  private createContext(): ActionBarContext {
    return {
      route: this.route,
      injector: this.injector,
      dataService: this.dataService,
      notificationService: this.notificationService,
      entity$: of(undefined)
    }
  }
}
